import type { TourProps } from 'antd'

export const config = {
  SCROLL_THRESHOLD: 50,
  DEBOUNCE_DELAY: 150,
  MAX_COMMENT_LENGTH: 250,
  CHILD_TABLE_HEIGHT: 400,
  ITEMS_PER_SCROLL: 20,
  ITEMS_PER_PAGE: 10,
  API_PATH: '/integrations/custom-dashboard',
  ADMIN_SCOPE: ['admin/common'],
  FRONTLINE_SCOPE: ['frontline/common'],
  IS_COMMENT_REQUIRED: ['Resolved', 'Support Required'],
}

export const MESSAGE = {
  SUPPORT_REQUEST_SUCCESS_TOAST: 'Your support request has been submitted successfully.',
  ADD_COMMENT_BEFORE_RECALL: 'Please add a comment before recalling this request.',
  COMMENT_IS_REQUIRED: 'A comment is required.',
  PLEASE_SELECT_ANY_OPTION: 'Please select at least one option.',
  PLEASE_GIVE_SOME_VALUE_FOR_SEARCH: 'Please enter a value to search.',
  DOWNLOAD_EXCEL_FILE: 'Preparing your Excel file...',
  DOWNLOAD_EXCEL_FILE_SUCCESS: 'Your Excel file has been downloaded successfully.',
  DOWNLOAD_EXCEL_FILE_FAILED: 'Failed to download the Excel file.',
  RESOLVE: 'Resolve',
  RECALL: 'Recall',
  ERROR: 'Error',
  RESOLVED: 'Resolved',
  SUPPORT_REQUIRED: 'Support Required',
  SUCCESS: 'Success',
  FAILED: 'Failed',
  RESOLVE_CONFIRMATION: 'Confirm Resolve',
  RECALL_CONFIRMATION: 'Confirm Recall',
  RESOLVED_CONFIRMATION_MESSAGE: 'Are you sure you want to mark this request as resolved?',
  RECALL_CONFIRMATION_MESSAGE: 'Are you sure you want to recall this request?',
  SUPPORT_REQUIRED_MESSAGE: 'This request will be marked as Support Required.',
  ADD_SUPPORT_REQUIRED: 'Submit',
  BUTTON_TEXT: 'Yes',
}

export const columnConfig = {
  id: 'Log Id',
  customer_name: 'Customer Name',
  integration_partner: 'Customer Portal Name',
  ken: 'Ken',
  // organizationName: 'Organization',
  purchase_order_number: 'Customer PO Number',
  service_order_number: 'Service Order Number',
  timestamp: 'timestamp',
  // country_name: 'Country',
  country: 'Country',
  activity_type: 'Activity Type',
}

export const INITIAL_SCROLL_STATE: ScrollState = {
  page: 1,
  loading: false,
}

export interface ParentRecord {
  key: number
  customer_name: string
  activity_type: string
  purchase_order_number: string
  integration_partner: string
  country: string
  statuscode: number
  total: number
  status: string
}

export interface ChildRecord {
  id: string
  key: string
  ken: string
  service_order_number: string
  timestamp: string
  requestData?: string
  url?: string
  metadata?: {
    request_data?: any
    requestPathParams?: any
    errorDetails?: any
    error_reason?: any
  }
  status: string
  activity_type: string
}

export interface ResolveDetails extends Partial<ParentRecord> {
  isAll?: boolean
  resolvedIds?: string[]
}

export interface ScrollState {
  page: number
  loading: boolean
}

export interface GroupedLogs {
  status: 'Error' | 'Success' | 'Resolved'
  country_name: string
  customer_name: string
  integration_partner: string
  purchase_order_number: string
  total: number
  statusCode: number
  activity_type: string
}

export type SelectedGroupedLogs = {
  id: string
  ken: string
  timestamp: string
  service_order_number: string
  error_reason: Record<string, any>
  request_data: Record<string, any>
  request_path_params: Record<string, any>
  event_body: Record<string, any>
  created_at: string
  updated_at: string
  metadata: any
} & GroupedLogs

export type Filters = {
  status: string
  type?: any
  search_term?: string
  start?: number
  limit?: number
  orderBy?: string
  range?:
  | {
    duration: 'today' | 'week' | 'month' | 'custom'
    from?: string
    to?: string
  }
  | Record<string, never>
  org_ids?: string[]
  integration_partner?: string[]
  countries?: string[]
  customer_name?: string[]
}

export type SelectedGroupFfilter = {
  purchase_order_number: string
  status_code: number
  activity_type: string
} & Filters

export type logHistory = {
  log_id: string
  start?: number
  limit?: number
  isAdmin: boolean
}

export type resolvedRequest = {
  log_ids?: string[]
  status_code?: string
  purchase_order_number?: string
  status: string
  new_status: string
  comment?: string
  isAdmin: boolean
  email?: string
  activity_type?: string
}

export type getFilters = {
  status: string
  org_ids?: string[]
  type?: 'country' | 'organization' | 'integration_partner' | 'customer_name' | undefined
}

export type ResolvedSupport = (ResolveDetails | ChildRecord) & { isSupport?: boolean }
export const flattenObject = (obj: any, parentKey = '', res: any = {}): any => {
  for (const key in obj) {
    const newKey = parentKey ? `${parentKey}.${key}` : key
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      flattenObject(obj[key], newKey, res)
    } else {
      res[newKey] = obj[key]
    }
  }
  return res
}

export const formatSection = (section: string): string =>
  section.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())

export const dashboardGuide: TourProps['steps'] = [
  {
    title: 'Welcome to the Customer Portal Dashboard!',
    description: (
      <div
        style={{
          color: 'white',
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
        }}
      >
        <ul>
          <li>
            The <strong>Customer Portal Dashboard</strong> provides a consolidated view of API failure events,
            displaying detailed error information <br /> for easy monitoring and analysis.
          </li>
          <li>
            It enables users to track and review all API failure occurrences, helping teams quickly identify issues,
            troubleshoot errors,
            <br /> and take corrective actions directly on the portal.
          </li>
          <li>
            This dashboard is particularly useful for the <strong>Front Line (FL)</strong> team to proactively monitor
            failure events and make informed <br />
            decisions to ensure accurate and timely updates.
          </li>
          <li>
            Overall, this dashboard serves as a central monitoring and management hub — empowering users to identify,
            analyze, <br />
            and resolve API-related issues effectively.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Dashboard Structure and Key Components',
    description: (
      <div
        style={{
          color: 'white',
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
        }}
      >
        <ul>
          <li>
            The errors displayed on this dashboard are logically grouped based on the following attributes:
            <ul>
              <li>
                <strong>Customer PO Number</strong> – The unique work order number from the customer portal.
              </li>
              <li>
                <strong>Activity Type</strong> – Predefined system code representing the type of maintenance visit
                provided.
              </li>
              <li>
                <strong>Status Code</strong> – The current processing status of the API request.
              </li>
            </ul>
          </li>
          <li>
            The dashboard contains four key tabs — <strong>Error</strong>, <strong>Support</strong>,{' '}
            <strong>Resolved</strong>, and <strong>Success</strong> — where records are categorized based on <br />
            their current state.
          </li>
          <li>
            At the top of each tab, the total count of grouped records is displayed, providing a quick summary of the
            data.
          </li>
          <li>
            Built-in <strong>Filters</strong> allow users to filter results based on various criteria, with an
            additional <strong>Advanced Filter</strong> option <br />
            for more specific data filtering.
          </li>
          <li>
            <strong>Action Buttons</strong> are available to search records using applied filters or to clear filters
            for resetting the view.
          </li>
          <li>
            The <strong>DOWNLOAD EXCEL</strong> button, located at the top right, allows users to export either all or
            filtered records in Excel format
            <br /> for offline review.
          </li>
          <li>
            <strong>Pagination</strong> controls are provided to navigate between <strong>Next</strong> and{' '}
            <strong>Previous</strong> pages, making it easy to browse large datasets
            <br /> efficiently.
          </li>
        </ul>
      </div>
    ),
  },
]

export const errorGuide: TourProps['steps'] = [
  {
    title: 'Error Tab Overview',
    description: (
      <div
        style={{
          color: 'white',
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
        }}
      >
        <ul>
          <li>
            This tab provides a comprehensive overview of all API-related errors displayed in the respective columns.
          </li>
          <li>
            Each column includes an option to <strong>Expand</strong> or <strong>Collapse</strong> to view individual
            error entries within that group.
          </li>
          <li>
            Under the <strong>Action</strong> column, the <strong>RESOLVE</strong> button allows you to resolve either
            the entire error group or specific individual errors
            <br /> as needed.
          </li>
          <li>
            In the <strong>Details</strong> column, the <strong>VIEW</strong> button displays detailed information about
            each error, along with its <strong>History</strong> and any <br />
            associated comments.
          </li>
          <li>
            Within the <strong>Support</strong> column, the <strong>Support</strong> button can be used to raise a
            support request with the Support Team, including
            <br /> relevant comments required for API resolution.
          </li>
        </ul>
      </div>
    ),
  },
]

export const supportGuide: TourProps['steps'] = [
  {
    title: 'Support Tab Overview',
    description: (
      <div
        style={{
          color: 'white',
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
        }}
      >
        <ul>
          <li>
            This tab displays all <strong>Error Groups</strong> that require <strong>Support</strong> intervention.
            These entries are moved here from the
            <br /> <strong>Error</strong> tab when a support request is raised.
          </li>
          <li>
            The <strong>VIEW</strong> button in the <strong>Details</strong> column allows you to review complete error
            information, including its <strong>History</strong>
            <br /> and related comments.
          </li>
          <li>
            The <strong>RESOLVE</strong> button under the <strong>Action</strong> column enables you to resolve either
            an entire error group or specific
            <br /> individual errors as appropriate.
          </li>
        </ul>
      </div>
    ),
  },
]

export const resolvedGuide: TourProps['steps'] = [
  {
    title: 'Resolved Tab Overview',
    description: (
      <div
        style={{
          color: 'white',
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
        }}
      >
        <ul>
          <li>
            This tab displays the complete list of <strong>RESOLVED</strong> errors that were previously addressed in
            either the <strong>Error</strong> or
            <br /> <strong>Support</strong> tabs.
          </li>
          <li>
            Under the <strong>Action</strong> column, a <strong>RECALL</strong> button is available for each error group
            to reopen a resolved error by
            <br /> providing the necessary comments.
          </li>
          <li>
            Once an error group or individual error is <strong>Recalled</strong>, it will automatically move from the
            <strong> Resolved</strong> tab back
            <br /> to the <strong>Error</strong> tab for further action.
          </li>
        </ul>
      </div>
    ),
  },
]

export const successGuide: TourProps['steps'] = [
  {
    title: 'Success Tab Overview',
    description: (
      <div
        style={{
          color: 'white',
          fontSize: '1rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'hidden',
        }}
      >
        <ul>
          <li>
            This tab displays all <strong>Successful</strong> records along with their corresponding details.
          </li>
          <li>
            Since these records have been successfully processed, options to <strong>Resolve</strong> or{' '}
            <strong>Recall</strong> are not available.
          </li>
          <li>
            The <strong>VIEW</strong> button in the <strong>Details</strong> column allows you to review complete record
            information, including its
            <br /> <strong>History</strong> and any associated comments.
          </li>
        </ul>
      </div>
    ),
  },
]
